import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import axios from '../utils/axios';
import localStorageAvailable from '../utils/localStorageAvailable';
//
import { isValidToken, setSession, ACCESS_TOKEN_STORAGE_KEY } from './utils';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'FORGOT_PASSWORD') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }

  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext(null);

// ----------------------------------------------------------------------

function normalizeUser(raw) {
  if (!raw) {
    return null;
  }
  return {
    ...raw,
    displayName: raw.name || raw.displayName || raw.email,
    role: 'Технічний суддя',
  };
}

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

const authHost = process.env.REACT_APP_HOST_API_KEY

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const storageAvailable = localStorageAvailable();

  const initialize = useCallback(async () => {
    try {
      const accessToken = storageAvailable ? localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY) : '';

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        const response = await axios.get(`${authHost}/superior-judge/auth/me`);
        const user = normalizeUser(response.data);

        dispatch({
          type: 'INITIAL',
          payload: {
            isAuthenticated: true,
            user,
          },
        });
      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: 'INITIAL',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, [storageAvailable]);

  useEffect(() => {
    initialize();
  }, [dispatch]);

  // LOGIN
  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${authHost}/superior-judge/auth/login`, {
      email,
      password,
    });
    const { token, superior_judge: rawUser } = response.data;
    if(!token){
      alert('Увага! Відсутній токен авторизації від сервера. Будь ласка, зверніться до адміністратора.')
    }
    setSession(token);

    dispatch({
      type: 'LOGIN',
      payload: {
        user: normalizeUser(rawUser),
      },
    });
  }, []);

  const forgotPassword = useCallback(async (email) => {
    const response = await axios.post(`${authHost}/auth/forgot-password`, {
      email
    });

    dispatch({
      type: 'FORGOT_PASSWORD',
      payload: {
      },
    });
  }, []);

  // REGISTER
  const register = useCallback(async (key, name, email) => {
    const response = await axios.post(`${authHost}/auth/register`, {
      key,
      name,
      email,
    });
    const { token, superior_judge: rawUser } = response.data;

    setSession(token);

    dispatch({
      type: 'REGISTER',
      payload: {
        user: normalizeUser(rawUser),
      },
    });
  }, []);

  // LOGOUT
  const logout = useCallback(() => {
    setSession(null);
    dispatch({
      type: 'LOGOUT',
    });
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      method: 'jwt',
      login,
      forgotPassword,
      register,
      logout,
    }),
    [state.isAuthenticated, state.isInitialized, state.user, login, forgotPassword, logout, register]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
